// Facade for the FERT formio form renderer
// API description: https://partner.bdr.de/confluence/display/BUN/Einbinden+des+Form-Renderers+zum+Anzeigen+von+Formularen

"use strict";

import {EventEmitter} from 'events'
import {_debug} from '../common/debugger/debugger';

interface IEmissions {
  connect: () => void
  initiated: (property: any) => void
  submit: (property: any) => void
  dataChange: (property: any) => void
  changedPage: (property: any) => void
}

class FormRenderer extends EventEmitter {

  private _untypedOn = this.on
  private _untypedEmit = this.emit
  public on = <K extends keyof IEmissions>(event: K, listener: IEmissions[K]): this => this._untypedOn(event, listener)
  public emit = <K extends keyof IEmissions>(event: K, ...args: Parameters<IEmissions[K]>): boolean => this._untypedEmit(event, ...args)

  HTMLReference;
  instance;
  options;

  constructor(elementID: string, definition?, userDecisions?, metaData?, userData?, uploadEndpoint?, catalogEndpoint?, i18n?) {
    super();

    this.HTMLReference = document.getElementById(elementID);

    this.options = JSON.stringify(this.createOptions(userDecisions, metaData, userData, uploadEndpoint, catalogEndpoint, i18n));
    let opts = JSON.parse(this.options);
    _debug('constructor() parsed opts:', opts);

    const additionalOpts = {
      usingOptionalApproval: true,
      usingFeedbackChannel: true,
      usingIntent: true,
      usingAgeCheck: true,
      usingLogin: true,
      usingWrittenForm: true,
      usingAttachmentIndex: false,
      usingSubmitButton: true,
      usingUnloadMessage: true,
      verifiedUserdataImmutable: true
    }

    opts = { ...opts, ...additionalOpts }
    _debug('constructor() opts with additionalOpts:', opts);

    //this.init(definition.layout, opts);
    this.initWhenFormioAvailable(definition.layout, opts)

  }

  retries = 0;

  initWhenFormioAvailable(layout, opts) {
    window.setTimeout(() => {
      _debug('initWhenFormioAvailable() checking readiness');
      if (window['Formio']) {
        this.init(layout, opts);
      } else {
        this.retries++
        if (this.retries < 200) {
          this.initWhenFormioAvailable(layout, opts);
        }
      }
    }, 10);
  }

  init(layout, opts) {
    (window as any).Formio.createForm(
      this.HTMLReference,
      JSON.parse(layout),
      opts
    ).then((form) => {
      // Prevent the submission from going to the form.io server
      // https://formio.github.io/formio.js/app/examples/customendpoint.html
      form.nosubmit = true;
      this.instance = form;
      _debug('formio instantiated');

      // catch the formio submit event and delegate to custum handler
      form.on("submit", (data) => {
        this.submit(data);
      });

      form.on('change', (changed) => {        
        setTimeout(() => {
          this.dataChange(changed);
          this.publishPageInfos()
        }, 10)

      });

      form.on('changedPage', (currentPageInfo) => {
        _debug('form.on(changedPage)', currentPageInfo);
        this.publishPageInfos(currentPageInfo)
      });

      this.initiated(this.options)
      this.publishPageInfos()
    });
  }

  publishPageInfos(currentPage?) {
    let infos = this.instance.getPageInfos()
    _debug('publishPageInfos(currentPage?) currentpage', currentPage);
    infos = { pageInfos: infos }
    if (currentPage) {
      infos['currentPage'] = currentPage;
    }
    this.changedPage(infos);
  }


  // create options to be passed to the form renderer
  createOptions(userDecisions?, metaData?, userData?, uploadEndpoint?, catalogEndpoint?, i18n?) {
    const options = {
      language: userDecisions.language
    };

    _debug('createOptions() metaData', metaData);
    if (metaData) {
      if (Object.keys(metaData).length > 0) {
        options["metaData"] = metaData;
      }
    }

    if (userData) {
      if (Object.keys(userData).length > 0) {
        options["userData"] = userData;
      }
    }

    if (uploadEndpoint) {
      options["uploadendpoint"] = uploadEndpoint;
    }

    if (catalogEndpoint) {
      options["catalog"] = catalogEndpoint;
    }

    options["asyncupload"] = true;

    if (userDecisions.offline === true) {
      options["offlineversand"] = true;
    }

    if (userDecisions.preview) {
      options["preview"] = true;
    }

    if (i18n) {
      options["i18n"] = i18n;
    }
    _debug('createOptions() resulting options', options);

    // feature flags:
    options["usingUnloadMessage"] = true; // no javascript confirm on site unload

    return options;
  }

  getData() { // FIXME unused function
    _debug('getData()');
    const data = this.instance.data;
    _debug('getData() data', data);
    return data
  }

  getTidyData() {
    _debug('getTidyData()');
    let data = null;
    if(this.instance && this.instance.getSavedSubmission()) {
      data = this.instance.getSavedSubmission().data;
    }

    _debug('getTidyData() data', data);
    return data
  }

  getPayload() {
    const payload = this.instance.createPayload();
    _debug('getPayload() payload', payload)
    return payload;
  }

  setData(data) { // FIXME unused function
    _debug('setData(data)', data);
    this.instance.submission = {
      data: data
    };
    return data
  }

  getDocumentIds() {
    let ids = [];
    _debug('getDocumentIds() this.instance.getDocumentIds:', this.instance.getDocumentIds);
    if (this.instance.getDocumentIds) {
      ids = this.instance.getDocumentIds();
    }
    _debug('getDocumentIds() ids', ids);
    return ids;
  }

  getAgeInfo() {
    let infos = [];
    _debug('getAgeInfo() this.instance.getAgeInfo:', this.instance.getAgeInfo);
    if (this.instance.getAgeInfo) {
      infos = this.instance.getAgeInfo();
    }
    _debug('getAgeInfo() infos', infos);
    return infos;
  }

  isFormValid() {
    _debug(this.instance.isFormValid())
    return this.instance.isFormValid()
  }

  needsOptionalApproval() {
    let optionalDataGiven = false;
    _debug('needsOptionalApproval() this.instance.needsOptionalApproval:', this.instance.needsOptionalApproval);
    if (this.instance.needsOptionalApproval) {
      optionalDataGiven = this.instance.needsOptionalApproval();
    }
    _debug('needsOptionalApproval() optionalDataGiven:', optionalDataGiven);
    return optionalDataGiven;
  }

  getAnlagenverzeichnis() {
    let attachments = false;
    _debug('getAnlagenverzeichnis() this.instance.getAnlagenverzeichnis:', this.instance.getAnlagenverzeichnis);
    if (this.instance.getAnlagenverzeichnis) {
      attachments = this.instance.getAnlagenverzeichnis();
    }
    _debug('getAnlagenverzeichnis() attachments:', attachments);
    /*if(attachments?.['optional']?.length === 0) { // FIXME for debug only! remove if implementation works
      return {
        required: [
            { label: "Ausweis", value: "Ausweis" },
            { label: "Perso", value: "Perso" },
        ],
        optional: [
            { label: "Ausweis (Optional)", value: "Ausweis" },
            { label: "Perso (Optional)", value: "Perso" },
        ],
      }
    }*/
    return attachments;
  }

  submit(data) {
    this.emit('submit', data)
  }

  dataChange(data) {
    this.emit('dataChange', data)
  }

  changedPage(data) {
    this.emit('changedPage', data)
  }

  initiated(options) {
    _debug('initiated() before emit:', options)
    this.emit('initiated', options)
  }

}

export { FormRenderer };